/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1702425600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Dec 13 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "monday",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Monday",
          "depth": 0,
          "id": "connect-to-monday"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Sync mode",
          "depth": 1,
          "id": "sync-mode"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h3: "h3",
      code: "code",
      ul: "ul",
      li: "li",
      img: "img"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API documentation"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Items"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to a Monday board as items"
            }), _jsx(_components.td, {
              children: "Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.monday.com/api-reference/docs/items_page",
                children: "Items"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-monday",
        children: "Connect to Monday"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Monday"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". Authenticate Hightouch to Monday by entering your API token from your Monday account."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't have an API token, follow the ", _jsxs(_components.a, {
          href: "https://developer.monday.com/api-reference/docs/authentication#accessing-api-tokens",
          children: ["instructions on ", _jsx(_components.strong, {
            children: "Monday"
          }), " to access API tokens"]
        }), " depending on your role. Ensure your API token has write access to the board you are syncing to."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Monday destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Monday destination you want to sync to."]
      }), "\n", _jsx(_components.p, {
        children: "Select which board you'd like to sync to. Each sync sends data to one board."
      }), "\n", _jsx(_components.h3, {
        id: "sync-mode",
        children: "Sync mode"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports ", _jsx(_components.strong, {
          children: "Update"
        }), " and ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode by using Monday's GraphQL API with version 2023-10.\n", _jsx(_components.strong, {
          children: "Update"
        }), " mode uses the ", _jsx(_components.a, {
          href: "https://developer.monday.com/api-reference/docs/columns#change-multiple-column-values",
          children: _jsx(_components.code, {
            children: "change_multiple_column_values"
          })
        }), " mutation.\n", _jsx(_components.strong, {
          children: "Insert"
        }), " mode uses the ", _jsx(_components.a, {
          href: "https://developer.monday.com/api-reference/docs/items#create-an-item",
          children: _jsx(_components.code, {
            children: "create_item"
          })
        }), " mutation."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports the following column types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Checkbox"
        }), "\n", _jsx(_components.li, {
          children: "Country"
        }), "\n", _jsx(_components.li, {
          children: "Date"
        }), "\n", _jsx(_components.li, {
          children: "Dependency"
        }), "\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "Hour"
        }), "\n", _jsx(_components.li, {
          children: "Link"
        }), "\n", _jsx(_components.li, {
          children: "Location"
        }), "\n", _jsx(_components.li, {
          children: "Name"
        }), "\n", _jsx(_components.li, {
          children: "Numbers"
        }), "\n", _jsx(_components.li, {
          children: "People"
        }), "\n", _jsx(_components.li, {
          children: "Phone"
        }), "\n", _jsx(_components.li, {
          children: "Rating"
        }), "\n", _jsx(_components.li, {
          children: "Status"
        }), "\n", _jsx(_components.li, {
          children: "Tags"
        }), "\n", _jsx(_components.li, {
          children: "Text"
        }), "\n", _jsx(_components.li, {
          children: "Timeline"
        }), "\n", _jsx(_components.li, {
          children: "Week"
        }), "\n", _jsx(_components.li, {
          children: "World Clock"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You may use Hightouch's object inline mapping to construct Monday's object values from your source. Below is an example sync configuration:\n", _jsx(_components.img, {
          src: "destinations/destination-monday-sample-sync-config.png",
          alt: ""
        })]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Monday uses ", _jsx(_components.strong, {
            children: "YYYY-MM-DD"
          }), " format for dates. For the most success in your sync, ensure that you use that format for your dates. You can also use Hightouch's template option to format your data."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Update mode"
        }), ", Hightouch requires a unique identifier in your model with item IDs from Monday. Rows without item IDs will fail to sync."]
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Insert mode"
        }), ", the source must contain a column that maps to the item name. Monday rejects any rows without an item name."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
